import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import CommonPageTitle from "../../components/CommonPageTitle";
import IconDisplay from "../../components/IconDisplay";
import useThemeHook from "../../hooks/useThemeHook";
import {
  Alert,
  AlertCircleOutline,
  CheckmarkDoneCircleOutline,
} from "react-ionicons";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { FONTSIZE, mainCryptoName, imagesUrls } from "../../../constants";
import { useTranslationHook } from "../../hooks/useTranslationHook";
import { THEME_COLORS } from "../../themes/DarkLight";
import { useNavigate } from "react-router-dom";
import { arthereumTokenomics } from "../../utils/ArthereumTokenomics";
import CommonButton from "../../components/CommonButton";
import TextField from "../../components/TextField";
import Text from "../../components/ThemedText";
import BoostDisplay from "../../components/BoostDisplay";
import CommonNavBar from "../../components/CommonNavBar";
import emitter, { EmitterEvents } from "../../utils/eventsEmitter";
import getUserSignInData from "../../utils/getUserSignInData";
import { actions } from "../../redux";
import eventBus from "../../../utils/eventBus";
import LoadingIndicator from "../../components/LoadingIndicator";

const StakeSomePiPage = ({}) => {
  const userData = useSelector((state) => (state as any).actualUser);
  const [amount, setAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [projectedBoost, setProjectedBoost] = useState(0);
  const [stakingTime] = useState(1);
useEffect(() => {
    eventBus.emit("fullscreen:enter");
    return () => {
      eventBus.emit("fullscreen:exit");
    };
  }, []);
  const theme = useThemeHook();
  const getTranslationText = useTranslationHook();

  // Initialize amount with user's balance
  useEffect(() => {
    const userBalance = userData?.user?.picoinBalance || 0;
    if (userBalance > 0) {
      setAmount(userBalance.toString());
      setProjectedBoost(arthereumTokenomics.calculateStakeBoost(userBalance.toString(), 1));
    }
  }, [userData?.user?.picoinBalance]);

  const validateInputs = () => {
    if (!amount || isNaN(Number(amount)) || Number(amount) < 0.1) {
      alert(
        getTranslationText("invalid_stake_amount", {
          min: 0.1,
          shortName: "PI",
        })
      );
      return false;
    }
    return true;
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const stake = async () => {
    if (isLoading) {
      return;
    }
    if (!validateInputs()) return;

    if (amount > (userData?.user?.picoinBalance ?? 0)) {
      emitter.emit(EmitterEvents.BOTTOM_SHEET_DISPLAY, {
        modal: {
          name: "alertModal",
          modalData: {
            customButton: {
              title: getTranslationText("deposit"),
              id: "deposit",
              bgColor: THEME_COLORS[theme].piPurple,
            },
            description: getTranslationText("insufficient_balance_desc", {
              crypto: "PiCoin",
            }),
            title: getTranslationText("insufficient_balance", {
              crypto: "PiCoin",
            }),
            icon: (
              <AlertCircleOutline
                color={THEME_COLORS[theme].red}
                height={"50px"}
                width={"50px"}
              />
            ),
          },
        },
      });
      return;
    }
    setIsLoading(true);
    try {
      await arthereumProvider.stakePI(userData.user.id, amount.toString());
      const user = await arthereumProvider.getUserModelDataByUid(userData.user.id);
      dispatch(actions.setActualUser(user));
      emitter.emit(EmitterEvents.BOTTOM_SHEET_DISPLAY, {
        modal: {
          name: "alertModal",
          modalData: {
            description: getTranslationText("stake_success_desc"),
            title: getTranslationText("stake_success", {
              amount,
              shortName: "PI",
            }),
            icon: (
              <CheckmarkDoneCircleOutline
                color={THEME_COLORS[theme].piPurple}
                height={"50px"}
                width={"50px"}
              />
            ),
          },
        },
      });
      navigate(-1);
      setTimeout(() => {
        emitter.emit(EmitterEvents.UNSTAKE);
      }, 500);
    } catch (error: any) {
      alert(error?.message || getTranslationText("stake_error"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountChange = (value: any) => {
    setAmount(value);
    setProjectedBoost(arthereumTokenomics.calculateStakeBoost(value, 1));
  };

  return (
    <>
      <CommonNavBar />
      <div className={`min-h-screen ${theme === "dark" ? "bg-gray-900" : "bg-gray-50"}`}>
        {/* Main Content - with bottom padding for fixed button */}
        <div className="max-w-2xl mx-auto px-6 py-8 md:py-12 pb-32 md:pb-12">
          {/* Header Section */}
          <div className="flex flex-col items-center mb-8">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-purple-500 opacity-20 blur-2xl rounded-full"></div>
              <img
                src={imagesUrls["arthereum"]}
                alt="Arthereum"
                className="relative w-24 h-24 md:w-32 md:h-32 animate-pulse"
              />
            </div>
            <h1 className={`text-2xl md:text-3xl font-bold mb-2 ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
              {getTranslationText("boost_mining_by_staking_pi")}
            </h1>
            <p className={`text-sm md:text-base text-center max-w-md ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
              {getTranslationText("boost_mining_by_staking_pi_desc", {
                mainCryptoName,
                crypto: "PiCoin",
              })}
            </p>
          </div>

          {/* Staking Card */}
          <div className="mb-8">
            <div className={`relative rounded-2xl p-6 md:p-8 border backdrop-blur-sm ${
              theme === "dark"
                ? "bg-gray-800 border-purple-500/30 shadow-xl shadow-purple-500/10"
                : "bg-white border-purple-300/50 shadow-lg"
            }`}>
              {/* Decorative gradient overlay */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-purple-500/10 to-transparent rounded-2xl pointer-events-none"></div>
              
              {/* Amount Input Section */}
              <div className="relative mb-6">
                <label className={`block text-sm font-medium mb-2 ${theme === "dark" ? "text-gray-300" : "text-gray-700"}`}>
                  {getTranslationText("pi_amount_to_stake")}
                </label>
                <TextField
                  multiline={false}
                  backgroundColor={theme === "dark" ? "rgba(55, 65, 81, 0.5)" : "rgba(243, 244, 246, 1)"}
                  text={amount}
                  placeHolder="0.00"
                  setText={handleAmountChange}
                />
                <div className="mt-2 flex justify-between items-center">
                  <span className={`text-xs ${theme === "dark" ? "text-gray-500" : "text-gray-600"}`}>
                    {getTranslationText("your_pi_balance", {
                      amount: userData?.user?.picoinBalance?.toFixed(2) || "0.00",
                      shortName: "PI",
                    })}
                  </span>
                  <span className={`text-xs font-semibold ${
                    (userData?.user?.picoinBalance ?? 0) < amount
                      ? "text-red-500"
                      : "text-green-500"
                  }`}>
                    {userData?.user?.picoinBalance?.toFixed(2) || "0.00"} PI
                  </span>
                </div>
              </div>

              {/* Divider */}
              <div className={`h-px mb-6 ${theme === "dark" ? "bg-gray-700" : "bg-gray-200"}`}></div>

              {/* Staking Details */}
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
                    {getTranslationText("staking_duration")}
                  </span>
                  <span className={`text-sm font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    {getTranslationText("month_amount", {
                      amount: stakingTime,
                    })}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
                    {getTranslationText("Boost_amount")}
                  </span>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-purple-600 bg-clip-text text-transparent">
                      {projectedBoost?.toFixed(2)}x
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
                    {getTranslationText("stake")}
                  </span>
                  <span className={`text-sm font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    {userData?.user?.username}
                  </span>
                </div>
              </div>

              {/* Tech Pattern Decoration */}
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent opacity-50"></div>
            </div>
          </div>

          {/* Footer Info - visible on desktop */}
          <div className={`hidden md:block mt-8 p-4 rounded-lg text-center ${
            theme === "dark" ? "bg-gray-800/50" : "bg-purple-50"
          }`}>
            <p className={`text-xs ${theme === "dark" ? "text-gray-500" : "text-gray-600"}`}>
              Secured by Arthereum Protocol
            </p>
          </div>
        </div>

        {/* Fixed Bottom Button Container - Only on Mobile */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 pb-safe">
          <div className={`${theme === "dark" ? "bg-gray-900" : "bg-gray-50"} border-t ${
            theme === "dark" ? "border-gray-800" : "border-gray-200"
          }`}>
            <div className="max-w-2xl mx-auto px-6 py-4">
              {isLoading && (
                <div className="flex justify-center mb-3">
                  <LoadingIndicator />
                </div>
              )}
              <CommonButton
                white={null}
                color={null}
                backgroundColor={THEME_COLORS[theme].piPurple}
                onClickHandle={stake}
                text={getTranslationText("stake")}
                padding={0}
              />
            </div>
          </div>
        </div>

        {/* Desktop Button - Not Fixed */}
        <div className="hidden md:block max-w-2xl mx-auto px-6 pb-12">
          {isLoading && (
            <div className="flex justify-center mb-4">
              <LoadingIndicator />
            </div>
          )}
          <CommonButton
            white={null}
            color={null}
            backgroundColor={THEME_COLORS[theme].piPurple}
            onClickHandle={stake}
            text={getTranslationText("stake")}
            padding={0}
          />
        </div>
      </div>
    </>
  );
};

export default StakeSomePiPage;